import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Mail, ArrowRight, ShoppingCart, Droplets, Filter, FlaskConical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import ContactCTA from '../components/ContactCTA';
import CatalogSearchBar from '../components/CatalogSearchBar';
import HelpBlock from '../components/HelpBlock';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPrice } from '../utils/formatPrice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "contact@atlantiquespaboutique.com";

// Helper to add to cart
const addToCart = (product) => {
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: 'entretien'
    });
  }
  
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
  
};

const subCategories = [
  { id: 'filtres', label: 'Filtres', icon: Filter },
  { id: 'produits-traitement', label: 'Produits de traitement', icon: FlaskConical },
  { id: 'analyse-eau', label: 'Analyse d\'eau', icon: Droplets },
];

const EntretienSpaPage = () => {
  const { subcategory } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(subcategory || 'all');
  const [searchResults, setSearchResults] = useState(null); // null = no search active

  // Update selected category when URL changes
  useEffect(() => {
    if (subcategory) {
      setSelectedCategory(subcategory);
    }
  }, [subcategory]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/parts`);
        // Filter for entretien products
        const entretien = response.data.filter(p => 
          p.main_category === 'entretien' ||
          p.sub_category?.includes('filtre') ||
          p.sub_category?.includes('traitement') ||
          p.sub_category?.includes('analyse') ||
          p.sub_category?.includes('testeurs') ||
          p.sub_category?.includes('chlore') ||
          p.sub_category?.includes('brome') ||
          p.sub_category?.includes('ph')
        );
        setProducts(entretien);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = searchResults !== null 
    ? searchResults // If search is active, use search results
    : products.filter(p => {
      // Filter by category only when no search is active
      if (selectedCategory !== 'all') {
        if (selectedCategory === 'analyse-eau') {
          if (p.sub_category !== 'analyse-eau' && p.sub_category !== 'testeurs-eau') return false;
        } else if (selectedCategory === 'filtres') {
          if (p.sub_category !== 'filtres') return false;
        } else if (p.sub_category !== selectedCategory) {
          return false;
        }
      }
      return true;
    });

  // Handle search results callback
  const handleSearchResults = (results) => {
    setSearchResults(results);
    // Reset category filter when searching
    if (results !== null) {
      setSelectedCategory('all');
    }
  };

  // JSON-LD for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Produits d'entretien pour spa",
    "description": "Produits d'entretien pour spa : filtres, produits de traitement, analyse d'eau. Maintenez une eau saine et cristalline.",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://atlantiquespaboutique.com/" },
        { "@type": "ListItem", "position": 2, "name": "Pièces & Accessoires", "item": "https://atlantiquespaboutique.com/pieces-accessoires" },
        { "@type": "ListItem", "position": 3, "name": "Entretien", "item": "https://atlantiquespaboutique.com/entretien-spa" }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="entretien-spa-page">
      <SEO 
        title="Produits d'entretien pour spa – Traitement et filtration | Atlantique Spa Boutique"
        description="Produits d'entretien pour spa : filtres de qualité, produits de traitement de l'eau, kits d'analyse. Maintenez une eau saine. Livraison France."
        canonical="/entretien-spa"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/pieces-accessoires">Pièces & Accessoires</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Entretien</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Hero Header - Full Width Image */}
      <FullWidthHero
        variant="compact"
        image="https://customer-assets.emergentagent.com/job_b3797b70-2366-4f7a-a978-30c05fd0fea5/artifacts/nmsjboa3_Sans%20titre%20-%202.jpg"
        imageAlt="Gamme complète de produits d'entretien HTH Spa - chlore, pH, anti-écume"
        badge="Eau saine & cristalline"
        badgeIcon={Droplets}
        title="Produits d'entretien pour spa"
        subtitle="Des solutions simples pour maintenir une eau saine et limpide. Produits de traitement adaptés, kits d'analyse... Tout pour un entretien facile de votre spa."
        overlayPosition="left"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        
        {/* SEO Intro Text */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <p className="text-slate-600 leading-relaxed">
            Un entretien régulier est essentiel pour profiter pleinement de votre spa. 
            Nous proposons une sélection de <strong>produits d'entretien de qualité</strong> : 
            filtres adaptés à votre équipement, produits de traitement (chlore, brome, oxygène actif), 
            et kits d'analyse pour surveiller la qualité de votre eau. 
            Des solutions simples pour un spa toujours prêt à l'emploi.
          </p>
        </div>

        {/* Unified Search Bar */}
        {/* Search Bar - Full Width Enhanced */}
        <div className="mb-6">
          <CatalogSearchBar 
            products={products}
            onSearchResults={handleSearchResults}
            placeholder="Rechercher une pièce ou une référence (ex : pompe LX WP250, clavier Balboa, filtre PRB50)"
            category="entretien"
            emptyIcon={Filter}
            showHelpText={true}
          />
        </div>

        {/* Help Block - Moved below search */}
        <div className="mb-6">
          <HelpBlock />
        </div>

        {/* Search Results Indicator */}
        {searchResults !== null && (
          <div className="mb-6 flex items-center justify-between bg-[#1E3A5F]/5 rounded-lg px-4 py-3">
            <p className="text-slate-700">
              <span className="font-semibold">{filteredProducts.length}</span> résultat{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
            </p>
            <button 
              onClick={() => setSearchResults(null)}
              className="text-sm text-[#1E3A5F] font-medium hover:underline"
            >
              Effacer la recherche
            </button>
          </div>
        )}

        {/* Category Filter - Hidden when search is active */}
        {searchResults === null && (
          <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Filtrer par type</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-[#1E3A5F] text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1E3A5F] hover:text-[#1E3A5F]'
              }`}
            >
              Tous
            </button>
            {subCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  selectedCategory === cat.id
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1E3A5F] hover:text-[#1E3A5F]'
                }`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="h-40 bg-slate-200 rounded-lg mb-4" />
                <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Droplets className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {searchResults !== null ? 'Aucun produit trouvé' : 'Produits bientôt disponibles'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchResults !== null 
                ? 'Essayez avec une autre référence, marque ou mot-clé.'
                : 'Nous préparons notre catalogue de produits d\'entretien.'
              }
            </p>
            <Link to="/contact">
              <Button className="bg-[#1E3A5F] hover:bg-[#152C4A]">
                Nous contacter pour un produit
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Card key={product.slug} className="overflow-hidden border border-slate-200 hover:border-[#1E3A5F]/30 hover:shadow-lg transition-all group" data-testid={`product-${product.slug}`}>
                <Link to={`/pieces-accessoires/${product.slug}`}>
                  <div className="relative h-40 bg-slate-100 flex items-center justify-center">
                    {product.images?.[0] ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.name}
                        className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <Filter className="w-12 h-12 text-slate-300" />
                    )}
                    {product.availability === 'en_stock' && (
                      <span className="absolute top-2 right-2 bg-emerald-500 text-white text-xs font-medium px-2 py-1 rounded">
                        En stock
                      </span>
                    )}
                  </div>
                </Link>
                <CardContent className="p-4">
                  <span className="text-xs text-slate-500 uppercase tracking-wide">{product.brand}</span>
                  <Link to={`/pieces-accessoires/${product.slug}`}>
                    <h3 className="font-semibold text-slate-900 text-sm mt-1 mb-2 line-clamp-2 group-hover:text-[#1E3A5F] transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  {product.price_ttc ? (
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-slate-900">{formatPrice(product.price_ttc)}</span>
                      <Button 
                        size="sm" 
                        onClick={() => addToCart(product)}
                        className="bg-[#1E3A5F] hover:bg-[#152C4A] h-8 px-3"
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Link to="/contact">
                      <Button variant="outline" size="sm" className="w-full">
                        Demander un prix
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* SEO Bottom Text */}
        <div className="mt-12 bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Conseils pour l'entretien de votre spa</h2>
          <div className="grid md:grid-cols-2 gap-6 text-slate-600">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Filtration régulière</h3>
              <p className="text-sm leading-relaxed">
                Nettoyez ou remplacez vos filtres régulièrement pour une eau toujours propre. 
                Un filtre encrassé réduit l'efficacité de la filtration et augmente la consommation électrique.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Équilibre de l'eau</h3>
              <p className="text-sm leading-relaxed">
                Contrôlez régulièrement le pH et le taux de désinfectant. 
                Une eau bien équilibrée est plus agréable et préserve les équipements de votre spa.
              </p>
            </div>
          </div>
        </div>

        {/* Cross-links to other categories */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Link to="/pieces-detachees-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Pièces détachées</span>
              <p className="text-xs text-slate-500">Pompes, réchauffeurs...</p>
            </div>
          </Link>
          <Link to="/accessoires-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Accessoires de spa</span>
              <p className="text-xs text-slate-500">Escaliers, couvertures...</p>
            </div>
          </Link>
          <Link to="/pompes-a-chaleur-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Pompes à chaleur</span>
              <p className="text-xs text-slate-500">3 kW, 5 kW, 7 kW</p>
            </div>
          </Link>
        </div>

        {/* Blog articles link */}
        <div className="mt-8 bg-emerald-50 rounded-xl border border-emerald-100 p-6">
          <h3 className="font-semibold text-slate-900 mb-2">Besoin de conseils d'entretien ?</h3>
          <p className="text-sm text-slate-600 mb-4">
            Consultez nos articles pour apprendre à entretenir votre spa comme un professionnel.
          </p>
          <Link to="/blog" className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700 transition-colors">
            Voir nos conseils d'entretien
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* CTA pièce spécifique */}
        <ContactCTA category="Produits entretien spa" />

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Questions sur l'entretien de votre spa ?
          </h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Notre équipe vous conseille sur les produits adaptés à votre équipement
          </p>
          <a href={`mailto:${EMAIL}`}>
            <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold gap-2">
              <Mail className="w-5 h-5" />
              Nous contacter
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default EntretienSpaPage;
